/**
 * Privacy – Privacybeleid (Fase 2 — uitgebreid, B2B-gewogen).
 */
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import {
  LEGAL_BTW,
  LEGAL_CONTACT_EMAIL,
  LEGAL_ENTITY_NAME,
  LEGAL_KVK,
  LEGAL_PRODUCT_NAME,
  getLegalAddressLines,
  legalMailtoHref,
} from '../lib/legal-site-info';

const LAST_UPDATED = '31 maart 2026';

export function Privacy() {
  const address = getLegalAddressLines().join(' · ');

  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/privacy"
        title="Privacybeleid"
        description={`Privacybeleid van ${LEGAL_PRODUCT_NAME} voor CalqTool™: gegevensverwerking, bewaartermijnen, cookies, rechten onder de AVG en contact voor privacyvragen.`}
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Privacy' }]} />
        <h1 className="mb-6 text-2xl font-bold text-text">Privacybeleid</h1>
        <div className="max-w-3xl space-y-6 rounded-lg border border-border bg-surface-card p-6">
          <p className="text-sm text-text-muted">Laatst bijgewerkt: {LAST_UPDATED}</p>

          <section>
            <h2 className="mb-2 font-semibold text-text">1. Wie zijn wij?</h2>
            <div className="space-y-2 text-sm text-text-muted">
              <p>
                <strong className="text-text">{LEGAL_ENTITY_NAME}</strong> (KvK: {LEGAL_KVK}) is de
                verwerkingsverantwoordelijke voor het platform <strong className="text-text">{LEGAL_PRODUCT_NAME}</strong>{' '}
                (bouwcalculaties, offertes en gerelateerde functionaliteit). Wij verwerken persoonsgegevens in
                overeenstemming met de Algemene verordening gegevensbescherming (AVG).
              </p>
              {address ? (
                <p>
                  Adres: <span className="text-text">{address}</span>
                </p>
              ) : null}
              <p>
                E-mail (o.a. privacyverzoeken):{' '}
                <a className="font-medium text-primary hover:underline" href={legalMailtoHref('Privacy / AVG')}>
                  {LEGAL_CONTACT_EMAIL}
                </a>
                . Meer op de{' '}
                <Link to="/contact" className="font-medium text-primary hover:underline">
                  contactpagina
                </Link>
                .
              </p>
              {LEGAL_BTW ? <p>BTW-identificatienummer: {LEGAL_BTW}</p> : null}
            </div>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-text">2. Voor wie is dit bedoeld? (B2B en consument)</h2>
            <div className="space-y-2 text-sm text-text-muted">
              <p>
                Onze dienst richt zich in de praktijk primair op{' '}
                <strong className="text-text">zakelijke gebruikers</strong> (ongeveer 90%): medewerkers of
                vertegenwoordigers van bedrijven en organisaties die {LEGAL_PRODUCT_NAME} namens hun organisatie gebruiken.
                Een beperkt deel van de gebruikers kan een <strong className="text-text">consument</strong> zijn
                (ongeveer 10%), bijvoorbeeld bij zelfstandigen zonder rechtspersoon of incidenteel particulier gebruik.
              </p>
              <p>
                Waar op u <strong className="text-text">dwingend consumentenrecht</strong> van toepassing is, gaat dat
                voor op tegenstrijdige bedingen in onze algemene voorwaarden. Zie ook de{' '}
                <Link to="/terms" className="font-medium text-primary hover:underline">
                  algemene voorwaarden
                </Link>
                .
              </p>
            </div>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-text">3. Welke gegevens verwerken we?</h2>
            <ul className="list-inside list-disc space-y-2 text-sm text-text-muted">
              <li>
                <strong className="text-text">Account:</strong> e-mailadres, (versleuteld) wachtwoord, rol binnen uw
                organisatie/tenant, optioneel bron van registratie (hoe u ons vond).
              </li>
              <li>
                <strong className="text-text">Dossiers en inhoud:</strong> projectnamen, bouw- en klantcontext,
                berekeningsdata, geüploade documenten/tekeningen die u per dossier opslaat (documentenbibliotheek), en —
                wanneer u daarvoor kiest — verwerking daarvan voor tekeninganalyse (waarbij inhoud naar een
                AI-dienstverlener kan worden gestuurd om resultaat te genereren).
              </li>
              <li>
                <strong className="text-text">Assistent (AI):</strong> berichten die u in de chat stuurt, om antwoord te
                kunnen genereren; threads kunnen worden opgeslagen volgens onze technische inrichting.
              </li>
              <li>
                <strong className="text-text">Technisch:</strong> IP-adres of afgeleide gegevens waar nodig voor
                beveiliging, fraudepreventie en rate limiting; sessie-identificatoren. Voor accountbeveiliging kan de
                webapplicatie u na een periode zonder gebruikersactiviteit automatisch afmelden (richtlijn: ongeveer{' '}
                <strong className="text-text">45 minuten</strong> zonder interactie), terwijl een sessietoken op de
                server technisch tot ongeveer <strong className="text-text">30 dagen</strong> kan blijven bestaan tenzij u
                uitlogt — dit sluit aan bij wat onder <strong className="text-text">Account → Beveiliging</strong> staat
                beschreven. Exacte tijden kunnen worden aangepast; de werking blijft gelijk.
              </li>
              <li>
                <strong className="text-text">Analytics (optioneel):</strong> alleen als u in de cookiebanner
                toestemming geeft: gebruiksstatistiek (o.a. paginaweergaves) naar onze eigen API — zie het{' '}
                <Link to="/cookies" className="font-medium text-primary hover:underline">
                  cookiebeleid
                </Link>
                .
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-text">4. Doeleinden en grondslagen</h2>
            <ul className="list-inside list-disc space-y-2 text-sm text-text-muted">
              <li>
                <strong className="text-text">Uitvoering van de overeenkomst</strong> (AVG art. 6 lid 1 onder b): account,
                dossiers, facturatie, support, e-mails die nodig zijn voor de dienst.
              </li>
              <li>
                <strong className="text-text">Gerechtvaardigd belang</strong> (art. 6 lid 1 onder f): beveiliging,
                fraudepreventie, verbetering en stabiliteit van het platform, statistiek in samenhang met contractuele
                relatie — waar nodig in evenwicht met uw belangen en met recht op bezwaar waar de AVG dat voorschrijft.
              </li>
              <li>
                <strong className="text-text">Toestemming</strong> (art. 6 lid 1 onder a): niet-noodzakelijke cookies /
                analytics via de cookiebanner, die u kunt intrekken.
              </li>
              <li>
                <strong className="text-text">Wettelijke verplichting</strong> (art. 6 lid 1 onder c): waar de wet
                bewaren of verstrekken vereist (bijv. fiscale administratie).
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-text">5. Bewaartermijnen</h2>
            <div className="space-y-2 text-sm text-text-muted">
              <p>
                <strong className="text-text">Account:</strong> zolang uw account bestaat en een beperkte periode
                daarna voor back-ups, geschillen en wettelijke verplichtingen (richtlijn: tot ongeveer 90 dagen na
                beëindiging, tenzij een langere termijn wettelijk vereist is).
              </p>
              <p>
                <strong className="text-text">Dossiers en inhoud:</strong> tot u ze verwijdert of het account wordt
                beëindigd, tenzij een wettelijke bewaarplicht anders voorschrijft.
              </p>
              <p>
                <strong className="text-text">Technische en beveiligingslogs:</strong> zolang nodig voor beveiliging en
                troubleshooting, daarna rotatie of verwijdering (typisch korter, bijv. weken tot enkele maanden, afhankelijk
                van configuratie).
              </p>
              <p>
                Exacte termijnen kunnen per omgeving verschillen. Voor een overzicht bij uw account: neem contact op via{' '}
                <a className="font-medium text-primary hover:underline" href={legalMailtoHref('Bewaartermijnen')}>
                  {LEGAL_CONTACT_EMAIL}
                </a>
                .
              </p>
            </div>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-text">6. Verwerkers en (sub)verwerkers</h2>
            <div className="space-y-3 text-sm text-text-muted">
              <p>
                Wij maken gebruik van zorgvuldig geselecteerde dienstverleners die persoonsgegevens in onze opdracht
                verwerken. Met hen sluiten wij — waar de AVG dat vereist — passende afspraken (verwerkersovereenkomsten).
                Geen verkoop van uw gegevens aan adverteerders.
              </p>
              <p className="font-medium text-text">Voorbeelden van categorieën en partijen (kan wijzigen):</p>
              <ul className="list-inside list-disc space-y-1">
                <li>
                  <strong className="text-text">Cloud / edge-infrastructuur:</strong> o.a.{' '}
                  <strong className="text-text">Cloudflare</strong> (o.a. hosting, database D1, Workers, Pages, objectopslag
                  R2, KV) — gegevens kunnen in datacenters binnen en buiten de Europese Economische Ruimte (EER) worden
                  verwerkt; zie hieronder bij doorgifte.
                </li>
                <li>
                  <strong className="text-text">Betalingen en abonnementen:</strong>{' '}
                  <strong className="text-text">Stripe</strong> als betaaldienstverlener (betaal- en factuurgegevens,
                  klantportaal, operationele e-mails over betalingen).
                </li>
                <li>
                  <strong className="text-text">AI / taalmodellen:</strong> o.a.{' '}
                  <strong className="text-text">OpenAI</strong> (of vergelijkbare aanbieders) voor de assistent en, waar u
                  daarvoor kiest, analyse van documentinhoud — alleen de u toegezonden invoer wordt daarvoor verwerkt
                  voor zover technisch nodig.
                </li>
                <li>
                  <strong className="text-text">E-mail:</strong> indien ingeschakeld voor transactionele e-mail, bijvoorbeeld
                  via <strong className="text-text">Resend</strong> of een vergelijkbare aanbieder.
                </li>
              </ul>
              <p>
                Een actuele lijst voor uw organisatie en een <strong className="text-text">verwerkersovereenkomst (DPA)</strong>{' '}
                kunt u opvragen via{' '}
                <a className="font-medium text-primary hover:underline" href={legalMailtoHref('DPA / subverwerkers')}>
                  {LEGAL_CONTACT_EMAIL}
                </a>
                . Voor zakelijke klanten is dat gebruikelijk onderdeel van contractuele borging naast dit privacybeleid.
              </p>
            </div>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-text">7. Doorgifte buiten de EER</h2>
            <p className="text-sm text-text-muted">
              Sommige verwerkers zijn gevestigd in landen buiten de EER (bijv. de Verenigde Staten) of kunnen daar
              gegevens verwerken. Waar nodig zorgen wij voor passende waarborgen in de zin van de AVG, zoals de door de
              Europese Commissie goedgekeurde standaardcontractbepalingen (SCC), aanvullende maatregelen waar passend, of
              een adequaatheidsbesluit. Details kunnen in de DPA en subverwerkersdocumentatie van de betreffende partij
              staan. Vragen:{' '}
              <a className="font-medium text-primary hover:underline" href={legalMailtoHref('Doorgifte buiten EER')}>
                {LEGAL_CONTACT_EMAIL}
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-text">8. Abonnement en betaling</h2>
            <div className="space-y-3 text-sm text-text-muted">
              <p>
                Abonnementsbetalingen voor standaardpakketten verlopen via <strong className="text-text">Stripe</strong>{' '}
                als betaaldienstverlener; wij bewaren doorgaans geen volledige kaartnummers. Afsluiting verloopt via Stripe
                Checkout; beheer van betaalmethode, facturen en opzegging kan via het{' '}
                <strong className="text-text">Stripe-klantportaal</strong> (door Stripe gehost). Na een sessie in dat
                portaal wordt u gebruikelijk teruggestuurd naar{' '}
                <Link to="/account/billing" className="font-medium text-primary hover:underline">
                  Abonnement &amp; facturatie
                </Link>{' '}
                in deze applicatie, overeenkomstig de omleidingsinstelling bij Stripe.
              </p>
              <p>
                Stripe kan u e-mails sturen die nodig zijn voor de uitvoering van de betalingsovereenkomst of op basis van
                gerechtvaardigd belang, waaronder mededelingen over mislukte incasso, openstaande authenticatie (zoals 3D
                Secure) en herinneringen — zoals in onze Stripe-instelling op{' '}
                <strong className="text-text">1, 3 en 5 dagen</strong> na het ontstaan van een openstaande
                betalingsbevestiging. Zie ook paragraaf 2 van de{' '}
                <Link to="/terms" className="font-medium text-primary hover:underline">
                  algemene voorwaarden
                </Link>{' '}
                en de{' '}
                <Link to="/prijzen" className="font-medium text-primary hover:underline">
                  pakkettenpagina
                </Link>
                .
              </p>
              <p>Factuurgegevens worden bewaard conform administratieve verplichtingen.</p>
            </div>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-text">9. Cookies</h2>
            <p className="text-sm text-text-muted">
              Zie het aparte{' '}
              <Link to="/cookies" className="font-medium text-primary hover:underline">
                cookiebeleid
              </Link>{' '}
              voor soorten cookies, analytics, inlogsessie en automatische afmelding na inactiviteit, en uw keuzes.
            </p>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-text">10. Uw rechten en hoe u ze uitoefent</h2>
            <div className="space-y-2 text-sm text-text-muted">
              <p>
                U heeft recht op <strong className="text-text">inzage</strong>, <strong className="text-text">rectificatie</strong>,{' '}
                <strong className="text-text">verwijdering</strong>, <strong className="text-text">beperking</strong> van de
                verwerking, <strong className="text-text">bezwaar</strong> (waar van toepassing) en — waar de AVG het
                voorschrijft — <strong className="text-text">dataportabiliteit</strong>. U kunt een klacht indienen bij de{' '}
                <strong className="text-text">Autoriteit Persoonsgegevens</strong> (www.autoriteitpersoonsgegevens.nl).
              </p>
              <p>
                Stuur verzoeken naar{' '}
                <a className="font-medium text-primary hover:underline" href={legalMailtoHref('AVG-verzoek')}>
                  {LEGAL_CONTACT_EMAIL}
                </a>{' '}
                of via de{' '}
                <Link to="/contact" className="font-medium text-primary hover:underline">
                  contactpagina
                </Link>
                , met vermelding van het e-mailadres van uw account en een korte omschrijving van uw verzoek. Wij
                beantwoorden in principe binnen <strong className="text-text">één maand</strong> (AVG); bij complexiteit of
                veel verzoeken mag die termijn eenmalig met twee maanden worden verlengd — daarvan ontvangt u bericht.
              </p>
              <p>
                Voor zakelijke accounts kan de contractpartner (uw werkgever of opdrachtgever) aanvullende afspraken
                hebben over wie verzoeken indient; wij helpen u desgewenst met verduidelijking.
              </p>
            </div>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-text">11. Beveiliging, back-ups en datalekken</h2>
            <div className="space-y-2 text-sm text-text-muted">
              <p>
                Wij treffen passende <strong className="text-text">technische en organisatorische maatregelen</strong>{' '}
                (zoals versleuteling in transport, toegangsbeperking, scheiding van omgevingen) passend bij het risico.
                Geen enkele dienst is echter 100% vrij van risico&apos;s.
              </p>
              <p>
                <strong className="text-text">Back-ups en herstel:</strong> wij maken gebruik van infrastructuur met
                ingebouwde redundantie en back-upmogelijkheden (o.a. database- en opslagdiensten van onze cloudprovider).
                Herstel na incidenten streven wij zorgvuldig na; concrete RPO/RTO kunnen per omgeving verschillen. Voor
                zakelijke afspraken (SLA, hersteltijden): neem contact op voor Enterprise-overeenkomsten.
              </p>
              <p>
                <strong className="text-text">Datalek:</strong> bij een inbreuk op de beveiliging van persoonsgegevens die
                waarschijnlijk een risico voor rechten en vrijheden oplevert, handelen wij in lijn met de AVG: waar
                vereist melden wij het bij de Autoriteit Persoonsgegevens en, indien nodig, informeren wij betrokkenen
                zonder onredelijke vertraging (in beginsel binnen 72 uur waar de wet dat voorschrijft).
              </p>
              <p>
                <strong className="text-text">Monitoring en incidenten:</strong> wij monitoren de dienst op storingen en
                beveiligingsaspecten voor zover technisch en redelijkerwijs mogelijk; bij incidenten volgen wij een intern
                afhandelingsproces (analyse, mitigatie, communicatie).
              </p>
            </div>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-text">12. Functionaris gegevensbescherming (FG)</h2>
            <p className="text-sm text-text-muted">
              Een <strong className="text-text">functionaris voor gegevensbescherming</strong> is alleen verplicht als dat
              op grond van de AVG voor onze organisatie geldt. Is dat niet het geval, dan zijn privacyverzoeken en vragen
              over verwerkingen te richten aan{' '}
              <a className="font-medium text-primary hover:underline" href={legalMailtoHref('Privacy / FG')}>
                {LEGAL_CONTACT_EMAIL}
              </a>
              . Als wij een FG benoemen, publiceren wij de contactgegevens hier en op de contactpagina.
            </p>
          </section>

          <section>
            <h2 className="mb-2 font-semibold text-text">13. Geen juridisch advies door deze website</h2>
            <p className="text-sm text-text-muted">
              Dit privacybeleid is bedoeld als transparante uitleg voor gebruikers. Het is{' '}
              <strong className="text-text">geen juridisch advies</strong> en vervangt geen individuele beoordeling door
              een advocaat of privacyprofessional. Wat juridisch leidend is voor uw organisatie, hangt af van uw
              concrete situatie (contract, sector, rol verwerkingsverantwoordelijke/subverwerker).{' '}
              <strong className="text-text">Ondertekende verwerkersovereenkomsten en afspraken met u</strong> gaan, waar
              van toepassing, vóór algemene websiteteksten.
            </p>
          </section>

          <Link to="/" className="inline-flex text-primary transition-colors duration-200 hover:underline">
            ← Terug naar home
          </Link>
        </div>
      </AnimatedSection>
    </div>
  );
}
